'use client'

import { Icon } from '@/components'
import { useCartPopup } from '@/contexts'
import Link from '@/features/ref/components/Link'
import { getCookieToken } from '@/libs/cookies'
import { useCartStore } from '@/store'
import { getVND } from '@/utils/helpers'
import {
  Badge,
  badgeClasses,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Skeleton,
  styled,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

const CartBadge = styled(Badge)`
  & .${badgeClasses.badge} {
    top: -7px;
    right: -7px;
    transform: translate(50%, -100%);
    font-size: 0.75rem;
  }
`
const StyledBox = styled(Box)(({ theme }) => ({
  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '6px',
    position: 'absolute',
    right: 0,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'transparent',
    borderRadius: '3px',
    '&:hover': {
      background: theme.palette.grey[600],
    },
  },
  '&::-webkit-scrollbar-thumb:active': {
    background: 'transparent',
  },

  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
}))

export const CartHeader = () => {
  const { cartId, cartItems, isLoading, removeCartItem, cartData, refetchCart } = useCartStore()
  const { anchorEl, anchorPosition, isTouch, handleClose, handleOpen, setIsTouch } = useCartPopup()
  const t = useTranslations()
  const popoverRef = useRef<HTMLDivElement>(null)

  // Make sure cartId is created only once
  const [isInitialized, setIsInitialized] = useState(false)
  useEffect(() => {
    if (!isInitialized && !cartId) {
      setIsInitialized(true)
      getCookieToken().then((token) => refetchCart())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, isInitialized])

  useEffect(() => {
    // Detect touch device
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    setIsTouch(isTouchDevice)
  }, [setIsTouch])

  // Close popover on page scroll
  useEffect(() => {
    const handleScroll = () => {
      if (anchorEl) {
        handleClose()
      }
    }

    if (anchorEl) {
      window.addEventListener('scroll', handleScroll, { passive: true })
      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [anchorEl, handleClose])

  const getVariantProduct = (item: IListCartItems) => {
    const listUid = item?.configurable_options?.map((el) => el?.configurable_product_option_value_uid)
    return item?.product?.variants?.find((item) =>
      item?.attributes?.every((attr) => listUid?.includes(attr.uid))
    )
  }

  return (
    <Box onMouseEnter={handleOpen} onMouseLeave={handleClose}>
      <IconButton
        onClick={handleOpen}
        sx={(theme) => ({
          background: theme.palette.primary.light,
          padding: '10px',
          '&:hover': { background: '#222' },
          position: 'relative',
        })}
      >
        <Icon.Cart />
        <CartBadge
          badgeContent={isLoading ? <CircularProgress size={15} color="inherit" /> : cartItems.length}
          color="primary"
          overlap="circular"
        />
      </IconButton>

      <Popover
        open={Boolean(anchorEl) && cartItems.length > 0}
        anchorReference={'anchorPosition'}
        anchorPosition={anchorPosition}
        onClose={handleClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        disableRestoreFocus
        disableScrollLock
        sx={{
          '& .MuiPopover-paper': {
            maxWidth: '95vw',
            minWidth: { xs: '90vw', sm: 400 },
          },
        }}
      >
        <Box ref={popoverRef} onMouseLeave={!isTouch ? handleClose : undefined}>
          <StyledBox sx={{ width: { xs: '90vw', sm: 400 }, maxHeight: 400, overflowY: 'auto' }}>
            {cartItems.length > 0 ? (
              cartItems.map((item) => (
                <Box
                  key={item.uid}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px',
                    color: (theme) => (theme.palette.mode === 'dark' ? '#fff' : '#0F172A'),
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      color: '#0F172A',
                      cursor: 'pointer',
                    },
                  }}
                >
                  <Image
                    src={item?.configured_variant?.image?.url ?? item?.product?.image?.url}
                    width={60}
                    height={60}
                    alt=""
                  />
                  <Box sx={{ flex: 1 }}>
                    <Tooltip title={item?.product?.name || ''} placement="top">
                      <Typography
                        component={'h5'}
                        sx={{
                          fontSize: 16,
                          fontWeight: 500,
                          lineHeight: '20px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          color: 'inherit',
                        }}
                      >
                        <Link
                          href={`/products/${getVariantProduct(item)?.product?.url_key || item?.product?.url_key}`}
                        >
                          {item?.product?.name}
                        </Link>
                      </Typography>
                    </Tooltip>
                    <Box sx={{ fontSize: 12, color: '#64748B' }}>
                      {item?.configurable_options?.map((el, index) => (
                        <span key={el?.option_label + el?.value_label}>
                          {index > 0 && ' - '}
                          {el?.value_label}
                        </span>
                      ))}
                    </Box>
                    <Box sx={{ fontSize: 12, color: '#64748B' }}>
                      {getVND(item?.prices?.price?.value)} x {item?.quantity}
                    </Box>
                    <strong>{getVND(item?.prices?.row_total?.value)}</strong>
                  </Box>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation()
                      removeCartItem(item?.uid)
                    }}
                    size="small"
                    sx={{ minWidth: 'auto', padding: 0 }}
                  >
                    <Image src={'/assets/images/icon_close.svg'} width={18} height={18} alt="Xóa sản phẩm khỏi giỏ hàng" />
                  </Button>
                </Box>
              ))
            ) : (
              <div style={{ padding: 10, textAlign: 'center' }}>
                {isLoading ? <Skeleton width="100%" height={50} /> : t('not-found')}
              </div>
            )}
          </StyledBox>
          {cartItems.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                borderTop: '1px solid #ddd',
                alignItems: 'center',
                padding: '10px',
                position: 'sticky',
                bottom: 0,
                // background: '#fff',
                color: (theme) => (theme.palette.mode === 'dark' ? '#fff' : '#64748B'),
              }}
            >
              <span style={{ fontSize: 14, color: 'inherit' }}>
                {t('Total')}: <strong>{getVND(cartData?.prices?.grand_total?.value)}</strong>
              </span>
              <Link href="/cart">
                <Button variant="contained" size="small" onClick={handleClose}>
                  {t('Product.cart')}
                </Button>
              </Link>
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  )
}
